// Two layers of SOP content, merged at render time:
//   1. Static stubs - markdown files under content/ glob-imported by Vite at
//      build time. These are the canonical baseline; deleting an override
//      reveals the stub again.
//   2. Live overrides - markdown strings stored in Cloudflare KV, fetched
//      once at app boot. An override either edits a stub (same slug) or
//      creates a brand new SOP (slug that doesn't match any stub).
//
// Components import `entries` (a Svelte derived store) and call the helper
// functions below with it. Updating the `overrides` store triggers a
// re-render across the app.

import { writable, derived, get } from "svelte/store";
import { parseFrontmatter } from "./frontmatter";
import type { Sop, SopCategory, SopStatus, SopType, DocLink } from "./types";

const sopFiles = import.meta.glob("/content/sops/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;

const cheatFiles = import.meta.glob("/content/cheat-sheets/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;

function fileSlug(path: string): string {
  const base = path.split("/").pop() ?? path;
  return base.replace(/\.md$/, "");
}

function asString(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}
function asNumber(v: unknown): number | undefined {
  return typeof v === "number" ? v : undefined;
}
function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function asStringArray(v: unknown): string[] {
  return asArray(v).map((x) => (typeof x === "string" ? x : "")).filter(Boolean);
}
function asDocs(v: unknown): DocLink[] {
  return asArray(v)
    .map((entry) => {
      if (entry && typeof entry === "object") {
        const e = entry as Record<string, unknown>;
        const title = asString(e.title, asString(e.url));
        const url = asString(e.url);
        if (url) return { title, url };
      }
      return null;
    })
    .filter((x): x is DocLink => x !== null);
}

const VALID_CATEGORIES: SopCategory[] = [
  "onboarding",
  "tooling",
  "client-delivery",
  "ads-ops",
  "sales",
  "internal",
];
const VALID_STATUSES: SopStatus[] = ["stub", "draft", "live", "archived"];

function parseSop(slug: string, raw: string, defaultType: SopType): Sop {
  const { data, body } = parseFrontmatter(raw);
  const finalSlug = asString(data.slug) || slug;
  const rawCategory = asString(data.category, "internal");
  const category = (VALID_CATEGORIES.includes(rawCategory as SopCategory)
    ? rawCategory
    : "internal") as SopCategory;
  const rawStatus = asString(data.status, "stub");
  const status = (VALID_STATUSES.includes(rawStatus as SopStatus)
    ? rawStatus
    : "stub") as SopStatus;
  const rawType = asString(data.type, defaultType);
  const type = (rawType === "cheat-sheet" ? "cheat-sheet" : "sop") as SopType;

  return {
    slug: finalSlug,
    title: asString(data.title) || finalSlug,
    category,
    type,
    tags: asStringArray(data.tags),
    owner: asString(data.owner, "team"),
    created: asString(data.created),
    updated: asString(data.updated),
    estimatedMinutes: asNumber(data.estimated_minutes),
    video: asString(data.video) || undefined,
    docs: asDocs(data.docs),
    related: asStringArray(data.related),
    status,
    description: asString(data.description) || undefined,
    body,
  };
}

// Static baseline - parsed once.
interface StubEntry {
  slug: string;
  raw: string;
  type: SopType;
}
const STUBS: StubEntry[] = [
  ...Object.entries(sopFiles).map(([path, raw]) => ({
    slug: fileSlug(path),
    raw,
    type: "sop" as SopType,
  })),
  ...Object.entries(cheatFiles).map(([path, raw]) => ({
    slug: fileSlug(path),
    raw,
    type: "cheat-sheet" as SopType,
  })),
];
const STUB_BY_SLUG = new Map(STUBS.map((s) => [s.slug, s]));

/** Slug -> raw markdown override string (from KV). */
export const overrides = writable<Record<string, string>>({});
/** Whether the initial fetch has completed (false = still loading or never tried). */
export const overridesLoaded = writable(false);

/** Merged set of all SOPs - derived from static stubs + overrides. */
export const entries = derived(overrides, ($overrides) => merge($overrides));

function merge(overrideMap: Record<string, string>): Sop[] {
  const out: Sop[] = [];
  // Override entries first - they win on slug collision.
  for (const [slug, raw] of Object.entries(overrideMap)) {
    const stub = STUB_BY_SLUG.get(slug);
    out.push(parseSop(slug, raw, stub?.type ?? "sop"));
  }
  for (const stub of STUBS) {
    if (overrideMap[stub.slug] !== undefined) continue;
    out.push(parseSop(stub.slug, stub.raw, stub.type));
  }
  return out.sort((a, b) => a.title.localeCompare(b.title));
}

/** Synchronous snapshot - safe outside reactive contexts. */
export function allEntries(): Sop[] {
  return get(entries);
}

export function bySlug(arr: Sop[], slug: string): Sop | undefined {
  return arr.find((s) => s.slug === slug);
}

export function byCategory(arr: Sop[], category: SopCategory): Sop[] {
  return arr.filter((s) => s.category === category);
}

export function byTag(arr: Sop[], tag: string): Sop[] {
  return arr.filter((s) => s.tags.includes(tag));
}

export function activeCategories(arr: Sop[]): SopCategory[] {
  const set = new Set<SopCategory>();
  for (const s of arr) set.add(s.category);
  return Array.from(set);
}

export function recentlyUpdated(arr: Sop[], days = 14): Sop[] {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return arr
    .filter((s) => {
      const t = Date.parse(s.updated);
      return !isNaN(t) && t >= cutoff;
    })
    .sort((a, b) => Date.parse(b.updated) - Date.parse(a.updated));
}

export function isStale(s: Sop, days = 180): boolean {
  if (!s.updated) return false;
  const t = Date.parse(s.updated);
  if (isNaN(t)) return false;
  return Date.now() - t > days * 24 * 60 * 60 * 1000;
}

export function relatedSops(arr: Sop[], s: Sop, max = 3): Sop[] {
  const explicit = s.related
    .map((slug) => bySlug(arr, slug))
    .filter((x): x is Sop => x !== undefined);
  if (explicit.length >= max) return explicit.slice(0, max);
  const fallback = byCategory(arr, s.category).filter(
    (x) => x.slug !== s.slug && !explicit.find((e) => e.slug === x.slug),
  );
  return [...explicit, ...fallback].slice(0, max);
}

/** True iff this slug exists as a static stub (vs. KV-only). */
export function hasStub(slug: string): boolean {
  return STUB_BY_SLUG.has(slug);
}

/** Returns the original stub markdown, or null if KV-only. */
export function getStub(slug: string): string | null {
  return STUB_BY_SLUG.get(slug)?.raw ?? null;
}
